import { AlertTriangle, CheckCircle2, Clock, ListTodo, Timer, CalendarClock } from 'lucide-react';
import React from 'react';
import { useGetTaskSummaryQuery } from '../../services/dashboardApi';
import { WidgetProps } from './types';
import { WidgetLoadingSkeleton, WidgetEmptyState } from './WidgetErrorBoundary';

const TasksWidget: React.FC<WidgetProps> = ({ scope, projectId, size }) => {
  const { data, isLoading, error } = useGetTaskSummaryQuery(
    { projectId: scope === 'project' ? projectId : undefined },
    { pollingInterval: 300000 } // 5 minute polling
  );

  if (isLoading) {
    return <WidgetLoadingSkeleton lines={4} />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 text-red-400 mx-auto mb-2" />
          <p className="text-red-600 text-sm font-medium">Failed to load tasks</p>
          <p className="text-gray-500 text-xs mt-1">Please try refreshing</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <WidgetEmptyState
        title="No Tasks"
        description="No tasks have been created yet"
        icon={ListTodo}
      />
    );
  }

  // Support both API response formats (byStatus vs direct properties)
  const taskStats = {
    total: data.total || 0,
    completed: data.completed || data.byStatus?.completed || 0,
    inProgress: data.inProgress || data.byStatus?.in_progress || 0,
    todo: data.todo || data.byStatus?.pending || 0,
    overdue: data.overdue || 0,
    dueSoon: data.dueSoon || data.dueThisWeek || 0,
    byPriority: data.byPriority || { critical: 0, high: 0, medium: 0, low: 0 },
  };

  const isCompact = size === '1x1';
  const completionPercent =
    taskStats.total > 0 ? Math.round((taskStats.completed / taskStats.total) * 100) : 0;

  return (
    <div className="space-y-4">
      {/* Completion Progress */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-gray-900">Task Progress</span>
          <span className="text-lg font-bold text-gray-900">{completionPercent}%</span>
        </div>
        <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full transition-all duration-500"
            style={{ width: `${completionPercent}%` }}
          />
        </div>
      </div>

      {/* Task Status Grid */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-green-50 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span className="text-xs font-medium text-gray-600">Completed</span>
          </div>
          <p className="text-xl font-bold text-green-700">{taskStats.completed}</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <Timer className="h-4 w-4 text-blue-600" />
            <span className="text-xs font-medium text-gray-600">In Progress</span>
          </div>
          <p className="text-xl font-bold text-blue-700">{taskStats.inProgress}</p>
        </div>
      </div>

      {/* Overdue & Due Soon */}
      <div className="grid grid-cols-2 gap-3">
        {taskStats.overdue > 0 && (
          <div className="bg-red-50 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-1">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <span className="text-xs font-medium text-gray-600">Overdue</span>
            </div>
            <p className="text-xl font-bold text-red-700">{taskStats.overdue}</p>
          </div>
        )}
        <div className={`bg-yellow-50 rounded-lg p-3 ${taskStats.overdue > 0 ? '' : 'col-span-2'}`}>
          <div className="flex items-center space-x-2 mb-1">
            <CalendarClock className="h-4 w-4 text-yellow-600" />
            <span className="text-xs font-medium text-gray-600">Due Soon</span>
          </div>
          <p className="text-xl font-bold text-yellow-700">{taskStats.dueSoon}</p>
        </div>
      </div>

      {/* To Do Count (for larger sizes) */}
      {!isCompact && taskStats.todo > 0 && (
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <Clock className="h-4 w-4 text-gray-600" />
            <span className="text-xs font-medium text-gray-600">To Do</span>
          </div>
          <p className="text-xl font-bold text-gray-700">{taskStats.todo}</p>
        </div>
      )}

      {/* Priority Breakdown (for larger sizes) */}
      {!isCompact && taskStats.byPriority && (
        <div className="pt-2 border-t border-gray-200">
          <p className="text-xs font-semibold text-gray-700 mb-2">By Priority</p>
          <div className="flex items-center flex-wrap gap-3">
            {taskStats.byPriority.critical > 0 && (
              <div className="flex items-center space-x-1.5">
                <div className="w-2 h-2 rounded-full bg-purple-500" />
                <span className="text-xs text-gray-600">
                  Critical: <span className="font-medium">{taskStats.byPriority.critical}</span>
                </span>
              </div>
            )}
            <div className="flex items-center space-x-1.5">
              <div className="w-2 h-2 rounded-full bg-red-500" />
              <span className="text-xs text-gray-600">
                High: <span className="font-medium">{taskStats.byPriority.high}</span>
              </span>
            </div>
            <div className="flex items-center space-x-1.5">
              <div className="w-2 h-2 rounded-full bg-yellow-500" />
              <span className="text-xs text-gray-600">
                Medium: <span className="font-medium">{taskStats.byPriority.medium}</span>
              </span>
            </div>
            <div className="flex items-center space-x-1.5">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-xs text-gray-600">
                Low: <span className="font-medium">{taskStats.byPriority.low}</span>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Total Summary */}
      <div className="pt-2 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          Total Tasks: <span className="font-semibold text-gray-900">{taskStats.total}</span>
        </p>
      </div>
    </div>
  );
};

export default TasksWidget;
